import { useEffect, useState } from "react";
import {
  getH3PromoPhase,
  H3_PROMO_END_MS,
  H3_PROMO_START_MS,
  h3PromoMsUntilEnd,
  h3PromoMsUntilStart,
  type H3PromoPhase,
} from "@/config/h3-promo";

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function formatCountdown(msLeft: number): string {
  const totalSeconds = Math.max(0, Math.ceil(msLeft / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function snapshot(now: number = Date.now()): { phase: H3PromoPhase; msLeft: number } {
  const phase = getH3PromoPhase(now);
  const msLeft =
    phase === "upcoming"
      ? h3PromoMsUntilStart(now)
      : phase === "active"
        ? h3PromoMsUntilEnd(now)
        : 0;
  return { phase, msLeft };
}

/**
 * Live countdown for the two-phase H3 flash promo. Ticks every second
 * through both the upcoming and active windows, so the UI flips from
 * "starts in …" to "ends in …" automatically at the hardcoded start
 * timestamp, and unmounts automatically at the end.
 */
export function useH3Promo() {
  const [state, setState] = useState<{ phase: H3PromoPhase; msLeft: number }>(() => snapshot());

  useEffect(() => {
    if (state.phase === "ended") return;
    const tick = () => setState(snapshot());
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
    // Intentionally runs once: the interval keeps ticking through the
    // upcoming → active transition without needing a re-arm.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    phase: state.phase,
    isAnnounced: state.phase === "upcoming" || state.phase === "active",
    isActive: state.phase === "active",
    isUpcoming: state.phase === "upcoming",
    msLeft: state.msLeft,
    countdownLabel: formatCountdown(state.msLeft),
    startsAt: H3_PROMO_START_MS,
    endsAt: H3_PROMO_END_MS,
  };
}
